import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';
import { CaseStatusBadge, CASE_STATUSES } from '../components/CaseStatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Users, Briefcase, FileText, AlertCircle, Plus, Search, Trash2, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);

  // Derive tab from URL path
  const pathSegment = location.pathname.split('/')[2] || '';
  const tab = pathSegment === 'cases' ? 'cases' : pathSegment === 'users' ? 'users' : 'overview';

  const handleTabChange = (newTab) => {
    if (newTab === 'overview') navigate('/admin');
    else if (newTab === 'cases') navigate('/admin/cases');
    else if (newTab === 'users') navigate('/admin/users');
  };

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/admin`, { withCredentials: true });
      setStats(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  return (
    <DashboardLayout>
      <div data-testid="admin-dashboard">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-heading">{t('dashboard')}</h1>
          <p className="text-[#475569] mt-1">{t('welcome_back')}, {user?.name}</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} label={t('total_users')} value={stats.total_users} color="blue" />
            <StatCard icon={Briefcase} label={t('total_cases')} value={stats.total_cases} color="purple" />
            <StatCard icon={Briefcase} label={t('active_cases')} value={stats.active_cases} color="green" />
            <StatCard icon={FileText} label={t('total_documents')} value={stats.total_documents} color="amber" />
          </div>
        )}

        <Tabs value={tab} onValueChange={handleTabChange}>
          <TabsList className="mb-6 bg-white border border-slate-200">
            <TabsTrigger value="overview" data-testid="tab-overview">{t('dashboard')}</TabsTrigger>
            <TabsTrigger value="cases" data-testid="tab-cases">{t('cases')}</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">{t('users')}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <CasesOverview stats={stats} />
          </TabsContent>
          <TabsContent value="cases">
            <CasesManager />
          </TabsContent>
          <TabsContent value="users">
            <UsersManager />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    blue: 'bg-[#E0F2FE] text-[#0284C7]',
    purple: 'bg-[#F3E8FF] text-[#7E22CE]',
    green: 'bg-[#D1FAE5] text-[#059669]',
    amber: 'bg-[#FEF3C7] text-[#D97706]',
  };
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
        <div className="text-2xl font-bold text-[#0F172A] font-heading">{value ?? '-'}</div>
        <div className="text-sm text-[#475569] mt-1">{label}</div>
      </CardContent>
    </Card>
  );
}

function CasesOverview({ stats }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  if (!stats) return null;

  const statusLabels = {
    new: t('status_new'), under_review: t('status_under_review'), in_progress: t('status_in_progress'),
    pending_documents: t('status_pending_documents'), resolved: t('status_resolved'), closed: t('status_closed'),
  };
  const statusBarColors = {
    new: 'bg-sky-500', under_review: 'bg-amber-500', in_progress: 'bg-purple-500',
    pending_documents: 'bg-red-500', resolved: 'bg-green-500', closed: 'bg-slate-400'
  };
  const maxCount = Math.max(...Object.values(stats.cases_by_status || {}), 1);

  return (
    <div className="space-y-6">
      {/* Monthly Summary Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-purple-50 to-white">
          <CardContent className="p-4">
            <p className="text-xs text-[#94A3B8] uppercase tracking-wider">{t('cases')} this month</p>
            <p className="text-2xl font-bold text-[#7E22CE] font-heading mt-1">{stats.cases_this_month}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-green-50 to-white">
          <CardContent className="p-4">
            <p className="text-xs text-[#94A3B8] uppercase tracking-wider">Resolved this month</p>
            <p className="text-2xl font-bold text-[#059669] font-heading mt-1">{stats.resolved_this_month}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-amber-50 to-white">
          <CardContent className="p-4">
            <p className="text-xs text-[#94A3B8] uppercase tracking-wider">Expected to finish</p>
            <p className="text-2xl font-bold text-[#D97706] font-heading mt-1">{stats.active_expected}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-4">
            <p className="text-xs text-[#94A3B8] uppercase tracking-wider">Monthly Revenue</p>
            <p className="text-2xl font-bold text-[#0284C7] font-heading mt-1">{stats.monthly_revenue} JOD</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-red-50 to-white">
          <CardContent className="p-4">
            <p className="text-xs text-[#94A3B8] uppercase tracking-wider">Pending Invoices</p>
            <p className="text-2xl font-bold text-[#DC2626] font-heading mt-1">{stats.pending_invoices}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Cases by Status */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="text-base font-heading">{t('cases')} {t('case_status')}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(stats.cases_by_status || {}).map(([status, count]) => (
              <div key={status}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#475569]">{statusLabels[status] || status}</span>
                  <span className="font-medium text-[#0F172A]">{count}</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5">
                  <div className={`h-1.5 rounded-full ${statusBarColors[status] || 'bg-slate-400'}`} style={{ width: `${(count / maxCount) * 100}%` }} />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Upcoming Reminders */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-[#B48E4B]" /> Upcoming Reminders
          </CardTitle></CardHeader>
          <CardContent>
            {(stats.upcoming_reminders || []).length > 0 ? (
              <div className="space-y-2">
                {stats.upcoming_reminders.map(r => (
                  <div key={r.reminder_id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg text-sm">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${r.reminder_type === 'hearing' ? 'bg-purple-500' : r.reminder_type === 'deadline' ? 'bg-red-500' : 'bg-blue-500'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#0F172A] truncate">{r.title}</p>
                      <p className="text-xs text-[#94A3B8]">{r.reminder_date} {r.reminder_time}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#94A3B8] text-center py-4">No upcoming reminders</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader><CardTitle className="text-base font-heading">Recent Activity</CardTitle></CardHeader>
          <CardContent>
            {(stats.recent_activity || []).length > 0 ? (
              <div className="space-y-2">
                {stats.recent_activity.map((log, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm p-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#B48E4B] flex-shrink-0 mt-1.5" />
                    <div>
                      <span className="font-medium text-[#0F172A]">{log.user_name}</span>
                      <span className="text-[#94A3B8]"> {log.action} </span>
                      <span className="text-[#475569]">{log.entity_type}</span>
                      <p className="text-xs text-[#94A3B8]">{new Date(log.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#94A3B8] text-center py-4">No recent activity</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Cases Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-base font-heading">Recent Cases</CardTitle></CardHeader>
        <Table>
          <TableHeader>
            <TableRow className="border-b border-slate-100">
              <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{t('case_title')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{t('client')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{t('lawyer')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{t('case_status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(stats.recent_cases || []).map(c => (
              <TableRow key={c.case_id} className="border-b border-slate-100 cursor-pointer hover:bg-slate-50"
                onClick={() => navigate(`/admin/cases/${c.case_id}`)}>
                <TableCell className="font-medium text-[#0F172A] text-sm">{c.title}</TableCell>
                <TableCell className="text-sm text-[#475569]">{c.client_name}</TableCell>
                <TableCell className="text-sm text-[#475569]">{c.lawyer_name || '-'}</TableCell>
                <TableCell><CaseStatusBadge status={c.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function CasesManager() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', case_type: 'corporate', client_id: '', lawyer_id: '' });
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [casesRes, usersRes] = await Promise.all([
        axios.get(`${API}/cases`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }),
      ]);
      setCases(casesRes.data);
      setClients(usersRes.data.filter(u => u.role === 'client'));
      setLawyers(usersRes.data.filter(u => u.role === 'lawyer'));
    } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/cases`, form, { withCredentials: true });
      setShowCreate(false);
      setForm({ title: '', description: '', case_type: 'corporate', client_id: '', lawyer_id: '' });
      fetchData();
    } catch {}
  };

  const filtered = cases.filter(c =>
    c.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.case_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input placeholder={t('search')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="ps-9 h-10" data-testid="cases-search" />
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button className="bg-[#B48E4B] text-white hover:bg-[#8C6A32]" data-testid="create-case-button">
              <Plus className="w-4 h-4 me-2" /> {t('new_case')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-[#0F172A]">{t('new_case')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>{t('case_title')}</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1" data-testid="case-title-input" />
              </div>
              <div>
                <Label>{t('case_description')}</Label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-1 w-full rounded-md border border-slate-200 p-3 text-sm min-h-[80px] focus:ring-2 focus:ring-[#B48E4B]/50 focus:outline-none" data-testid="case-description-input" />
              </div>
              <div>
                <Label>{t('case_type')}</Label>
                <Select value={form.case_type} onValueChange={v => setForm({...form, case_type: v})}>
                  <SelectTrigger className="mt-1" data-testid="case-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="corporate">Corporate</SelectItem>
                    <SelectItem value="civil">Civil</SelectItem>
                    <SelectItem value="criminal">Criminal</SelectItem>
                    <SelectItem value="family">Family</SelectItem>
                    <SelectItem value="real_estate">Real Estate</SelectItem>
                    <SelectItem value="ip">Intellectual Property</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('select_client')}</Label>
                <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})}>
                  <SelectTrigger className="mt-1" data-testid="case-client-select"><SelectValue placeholder={t('select_client')} /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.user_id} value={c.user_id}>{c.name} ({c.email})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('select_lawyer')}</Label>
                <Select value={form.lawyer_id} onValueChange={v => setForm({...form, lawyer_id: v})}>
                  <SelectTrigger className="mt-1" data-testid="case-lawyer-select"><SelectValue placeholder={t('select_lawyer')} /></SelectTrigger>
                  <SelectContent>
                    {lawyers.map(l => <SelectItem key={l.user_id} value={l.user_id}>{l.name} ({l.email})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleCreate} className="w-full bg-[#0F172A] text-white hover:bg-[#1E293B]" data-testid="submit-create-case">{t('create_case')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-slate-100">
              <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">ID</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{t('case_title')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{t('case_type')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{t('case_status')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{t('client')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(c => (
              <TableRow
                key={c.case_id}
                className="border-b border-slate-100 cursor-pointer hover:bg-slate-50"
                onClick={() => navigate(`/admin/cases/${c.case_id}`)}
                data-testid={`case-row-${c.case_id}`}
              >
                <TableCell className="text-xs text-[#94A3B8] font-mono">{c.case_id?.slice(0, 12)}</TableCell>
                <TableCell className="font-medium text-[#0F172A]">{c.title}</TableCell>
                <TableCell className="text-[#475569] capitalize">{c.case_type}</TableCell>
                <TableCell><CaseStatusBadge status={c.status} /></TableCell>
                <TableCell className="text-[#475569]">{c.client_id?.slice(0, 12)}</TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-[#94A3B8]">{t('no_cases')}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function UsersManager() {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'client', phone: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/users`, form, { withCredentials: true });
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', role: 'client', phone: '' });
      fetchUsers();
    } catch {}
  };

  const handleUpdate = async () => {
    if (!editUser) return;
    try {
      await axios.put(`${API}/users/${editUser.user_id}`, editForm, { withCredentials: true });
      setEditUser(null);
      fetchUsers();
    } catch {}
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      fetchUsers();
    } catch {}
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input placeholder={t('search')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="ps-9 h-10" data-testid="users-search" />
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button className="bg-[#B48E4B] text-white hover:bg-[#8C6A32]" data-testid="create-user-button">
              <Plus className="w-4 h-4 me-2" /> {t('create_user')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-[#0F172A]">{t('create_user')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>{t('name')}</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1" data-testid="user-name-input" />
              </div>
              <div>
                <Label>{t('email')}</Label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1" data-testid="user-email-input" />
              </div>
              <div>
                <Label>{t('password')}</Label>
                <Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="mt-1" data-testid="user-password-input" />
              </div>
              <div>
                <Label>{t('role')}</Label>
                <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                  <SelectTrigger className="mt-1" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">{t('admin')}</SelectItem>
                    <SelectItem value="lawyer">{t('lawyer')}</SelectItem>
                    <SelectItem value="client">{t('client')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('phone')}</Label>
                <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1" data-testid="user-phone-input" />
              </div>
              <Button onClick={handleCreate} className="w-full bg-[#0F172A] text-white hover:bg-[#1E293B]" data-testid="submit-create-user">{t('create_user')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editUser} onOpenChange={(open) => { if (!open) setEditUser(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-[#0F172A]">{t('edit')} {editUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>{t('name')}</Label>
              <Input value={editForm.name || ''} onChange={e => setEditForm({...editForm, name: e.target.value})} className="mt-1" />
            </div>
            <div>
              <Label>{t('role')}</Label>
              <Select value={editForm.role || ''} onValueChange={v => setEditForm({...editForm, role: v})}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">{t('admin')}</SelectItem>
                  <SelectItem value="lawyer">{t('lawyer')}</SelectItem>
                  <SelectItem value="client">{t('client')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('phone')}</Label>
              <Input value={editForm.phone || ''} onChange={e => setEditForm({...editForm, phone: e.target.value})} className="mt-1" />
            </div>
            <Button onClick={handleUpdate} className="w-full bg-[#0F172A] text-white hover:bg-[#1E293B]">{t('save')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Card className="border-slate-200 shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-slate-100">
              <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{t('name')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{t('email')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{t('role')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{t('phone')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(u => (
              <TableRow key={u.user_id} className="border-b border-slate-100" data-testid={`user-row-${u.user_id}`}>
                <TableCell className="font-medium text-[#0F172A]">{u.name}</TableCell>
                <TableCell className="text-[#475569]">{u.email}</TableCell>
                <TableCell>
                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium capitalize ${
                    u.role === 'admin' ? 'bg-[#0F172A] text-white' : u.role === 'lawyer' ? 'bg-[#B48E4B]/10 text-[#B48E4B]' : 'bg-slate-100 text-[#475569]'
                  }`}>{t(u.role)}</span>
                </TableCell>
                <TableCell className="text-[#475569]">{u.phone || '-'}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <button onClick={() => { setEditUser(u); setEditForm({ name: u.name, role: u.role, phone: u.phone }); }} className="p-1.5 rounded hover:bg-slate-100 text-[#475569]" data-testid={`edit-user-${u.user_id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(u.user_id)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`delete-user-${u.user_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
